// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useTheme } from '@mui/material/styles';
import { ThemeProvider } from '@mui/system';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { SnackbarProvider, closeSnackbar } from 'notistack';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router';
import { ErrorSnackbar, InfoSnackbar, SuccessSnackbar, WarningSnackbar } from './components/common/snackbar';
import { AppBar } from './components/main/appBar';
import { AppFooter } from './components/main/appFooter';
import { AppContextProvider, type AppContextType, useAppOptions } from './context/AppContext';
import { ColorModeContext } from './context/ColorModeContext';
import { useLoggedInState } from './context/LoggedInContext';
import { useUserContext } from './context/UserContext';
import ErrorBoundary from './errors/ErrorBoundary';
import { Box, CssBaseline, IconButton } from './lib/base';
import { useMediaQuery } from './lib/base';
import { CloseIcon } from './lib/icons';
import createSeedPlannerTheme from './theme/Theme';

interface AppShellCoreProps {
  readonly children?: React.ReactNode;
}
function AppShellCore({ children }: AppShellCoreProps) {
  const theme = useTheme();
  const appOptions = useAppOptions();
  const location = useLocation();
  const { title } = appOptions.meta;
  const path = location.pathname.endsWith('/') ? location.pathname.slice(0, -1) : location.pathname;
  const canonicalURL = `${appOptions.url}${path}`;

  return (
    <>
      <Helmet titleTemplate={`%s - ${title}`} defaultTitle={title}>
        <html lang="en" />
        <meta name="application-name" content={appOptions.meta.name} />
        <meta name="description" content={appOptions.meta.description} />
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta property="og:title" content={appOptions.meta.title} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalURL} />
        <meta property="og:image" content={`${appOptions.url}/og-image-facebook.jpg`} />
        <meta property="og:image:alt" content={appOptions.meta.name} />
        <meta property="og:image:width" content="3011" />
        <meta property="og:image:height" content="3011" />
        <meta property="og:description" content={appOptions.meta.description} />
        <meta property="og:site_name" content={appOptions.meta.name} />
        <meta property="fb:app_id" content={appOptions.facebookAppId} />
        <meta name="twitter:title" content={appOptions.meta.title} />
        <meta name="twitter:description" content={appOptions.meta.description} />
        <meta name="twitter:image" content={`${appOptions.url}/og-image-twitter.jpg`} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:image:alt" content={appOptions.meta.name} />"
        <meta name="twitter:site" content={appOptions.meta.twitter.handle} />
        <meta name="twitter:creator" content={appOptions.meta.twitter.creatorHandle} />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="manifest" href="/manifest.json" />
        <link rel="mask-icon" href="/safari-pinned-tab.svg" color={theme.palette.secondary.light} />
        <meta name="theme-color" content={theme.palette.primary.main} />
        <link rel="canonical" href={canonicalURL} />
        <script type="application/ld+json">
          {`{
              "@context": "http://schema.org",
              "@type": "WebSite",
              "name": "${appOptions.meta.name}",
              "url": "${canonicalURL}"
            }`}
        </script>
      </Helmet>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <AppBar />
        <ErrorBoundary>{children}</ErrorBoundary>
        <AppFooter appOptions={appOptions} />
        <SnackbarProvider
          autoHideDuration={4000}
          Components={{
            success: SuccessSnackbar,
            error: ErrorSnackbar,
            info: InfoSnackbar,
            warning: WarningSnackbar,
          }}
          action={(snackId) => (
            <IconButton aria-label="close-snackbar" onClick={() => closeSnackbar(snackId)}>
              <CloseIcon sx={{ color: 'white' }} />
            </IconButton>
          )}
        />
      </Box>
    </>
  );
}

function usePrefersDarkMode() {
  const { isLoggedIn } = useLoggedInState();
  const darkModeSystem = useMediaQuery('(prefers-color-scheme: dark)');
  const { darkModePreference } = useUserContext();

  return React.useMemo(() => {
    // If not logged in then get the preference from the browser
    if (!isLoggedIn) {
      return darkModeSystem;
    }
    // If they are logged in, then get the user context and check preference
    if (darkModePreference === 'system') {
      return darkModeSystem;
    }
    return darkModePreference === 'dark';
  }, [isLoggedIn, darkModeSystem, darkModePreference]);
}

interface Props {
  readonly appContext: AppContextType;
  readonly children?: React.ReactNode;
}
export default function AppShell({ appContext, children }: Props) {
  const prefersDarkMode = usePrefersDarkMode();
  const [mode, setMode] = React.useState<'light' | 'dark'>(prefersDarkMode ? 'dark' : 'light');
  React.useEffect(() => {
    setMode(prefersDarkMode ? 'dark' : 'light');
  }, [prefersDarkMode]);
  const colorMode = React.useMemo(
    () => ({
      mode,
      toggleMode: () => {
        const newMode = mode === 'light' ? 'dark' : 'light';
        setMode(newMode);
      },
    }),
    [mode],
  );
  const theme = React.useMemo(() => createSeedPlannerTheme(mode), [mode]);
  return (
    <React.StrictMode>
      <AppContextProvider value={appContext}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ColorModeContext.Provider value={colorMode}>
            <ThemeProvider theme={theme}>
              <CssBaseline enableColorScheme />
              <AppShellCore>{children}</AppShellCore>
            </ThemeProvider>
          </ColorModeContext.Provider>
        </LocalizationProvider>
      </AppContextProvider>
    </React.StrictMode>
  );
}
